import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button, Heading, Step, StepProps, useStepByStep } from 'yoso';
import { BaseCard } from '~/components/Card';
import { T } from '~/components/I18n/Translation';
import folder from '~/components/Icon/folder.png';
import {
  useParticipateLand,
  useParticipateLandErrorHandler,
} from '~/components/Land/Participation/LandParticipation.hooks';
import { useZone } from '~/components/Land/Zone/LandZone.hooks';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { Lup } from '~/components/Lup/Lup';
import { BottomSheetProps } from '~/components/Modal/BottomSheet';
import { Stepper } from '~/components/Stepper/Stepper';
import { format } from '~/components/Util/number';
import snapshotStyles from '../Fee/ParticipationFee.module.scss';
import stepStyles from '../Step/ParticipationStep.module.scss';

export type ParticipationSnapshotStepProps = Omit<StepProps, 'name'> & {
  className?: string;
  landId: number;
  zoneId: number;
  onDismiss?: BottomSheetProps['onDismiss'];
};

export const ParticipationSnapshotStep = ({
  className,
  landId,
  zoneId,
  onDismiss,
  ...props
}: ParticipationSnapshotStepProps) => {
  const handleError = useParticipateLandErrorHandler();
  const { steps, forward } = useStepByStep();
  const stepIdx = useMemo(() => steps.indexOf('SNAPSHOT'), [steps]);

  const queryClient = useQueryClient();
  const onSuccess = useCallback(() => {
    forward();
    queryClient.invalidateQueries(['land', landId]);
  }, [forward, landId, queryClient]);

  const { mutateAsync: participate } = useParticipateLand({
    onSuccess,
    onError: async (error) => {
      await handleError(error);
      onDismiss?.();
    },
  });

  const onClick = useCallback(async () => {
    await participate({ landId, zoneId });
  }, [landId, participate, zoneId]);

  const {
    data: {
      snapshot: { citizenNftLevel, citizenNftCount, levelUpPassCount },
    },
  } = useZone({ landId, zoneId });

  return (
    <Step name="SNAPSHOT" {...props}>
      <div className={classNames(stepStyles.root, className)}>
        <div className={stepStyles.header}>
          <Stepper className={stepStyles.stepper} activeStep={stepIdx} length={steps.length} />
        </div>
        <div className={stepStyles.body}>
          <img className={stepStyles.image} alt="step2" src={folder} width={100} height={100} />
          <Heading className={stepStyles.title} level={5}>
            <T name="citizen_land_join_snapshot_popup_title" />
          </Heading>
          <p className={stepStyles.description}>
            <T name="citizen_land_join_sec2_desc" balance />
          </p>
          <BaseCard className={snapshotStyles.root}>
            <DescriptionList className={snapshotStyles.descriptionList} layout="horizontal">
              <dt>LUP</dt>
              <dd>
                <Lup value={levelUpPassCount} zeroAsEmpty={true} />
              </dd>
              <dt>Lv{citizenNftLevel} Citizens</dt>
              <dd>
                {format(citizenNftCount)}{' '}
                <T name="citizen_nft_unit" options={{ count: citizenNftCount }} />
              </dd>
            </DescriptionList>
          </BaseCard>
        </div>
        <div className={stepStyles.footer}>
          <Button
            className={stepStyles.button}
            color="primary"
            onClick={onClick}
            data-event="click"
            data-event-target="land_popup_step2_cta_btn">
            <T name="citizen_join_button" />
          </Button>
        </div>
      </div>
    </Step>
  );
};
